import React, { useState, useEffect, Fragment, useMemo } from "react";
import PortalMenu from "../Common/PortalMenu";
import { Row, Col, Card, Table, Button, Badge, Form, Dropdown, Modal } from "react-bootstrap";
import { useNavigate, useSearchParams } from "react-router-dom";
import PageTitle from "../../layouts/PageTitle";
import api from "../../../api";
import swal from "sweetalert";
import Select from "react-select";
import SignatureModal from "../Common/SignatureModal";
import { useSelector } from "react-redux";
import PermissionWrapper from "../PermissionWrapper";
import VerifiedCopyActions from "../Common/VerifiedCopyActions";

const MeasurementSheetList = () => {
 const navigate = useNavigate();
 const user = useSelector((state) => state.auth?.userDetails || {});
 const [searchParams, setSearchParams] = useSearchParams();
 const [sheets, setSheets] = useState([]);
 const [loading, setLoading] = useState(true);
 const [previewing, setPreviewing] = useState(false);
 const [projects, setProjects] = useState([]);
 const [workOrders, setWorkOrders] = useState([]);
 const [showSignModal, setShowSignModal] = useState(false);
 const [approveId, setApproveId] = useState(null);
 const [generatingJmr, setGeneratingJmr] = useState(false);
 const [showAutoGenerateModal, setShowAutoGenerateModal] = useState(false);
 const [autoGenerateSheetType, setAutoGenerateSheetType] = useState("");
 const [autoGeneratePreview, setAutoGeneratePreview] = useState(null);
 const [previewLoading, setPreviewLoading] = useState(false);
 const [sheetTypes, setSheetTypes] = useState([]);

 const filters = {
 project: searchParams.get("project") || "",
 workOrder: searchParams.get("workOrder") || "",
 status: searchParams.get("status") || "",
 fromDate: searchParams.get("fromDate") || "",
 toDate: searchParams.get("toDate") || "",
 search: searchParams.get("search") || "",
 page: parseInt(searchParams.get("page") || "1"),
 limit: parseInt(searchParams.get("limit") || "10"),
 };

 useEffect(() => {
    const hasAnyParam = Array.from(searchParams.keys()).length > 0;
    if (!hasAnyParam) {
      try {
        const saved = sessionStorage.getItem("measurement_sheets_params");
        if (saved) {
          const parsed = JSON.parse(saved);
          if (Object.keys(parsed).length > 0) {
            setSearchParams(parsed);
            return;
          }
        }
      } catch (_) {}
    } else {
      try {
        const obj = {};
        searchParams.forEach((val, key) => { obj[key] = val; });
        sessionStorage.setItem("measurement_sheets_params", JSON.stringify(obj));
      } catch (_) {}
    }
  }, [searchParams]);

  useEffect(() => {
    fetchProjects();
    fetchWorkOrders();
    fetchSheets();
    fetchSheetTypes();
  }, [searchParams]);

 const fetchSheetTypes = async () => {
 try {
 const res = await api.get("/measurement-sheet-types");
 if (res.data.success) {
 const types = res.data.data || [];
 setSheetTypes(types);
 if (types.length > 0) setAutoGenerateSheetType(types[0].code);
 }
 } catch (err) {
 console.error("Failed to fetch sheet types", err);
 }
 };

 const fetchProjects = async () => {
 const res = await api.get("/projects?limit=1000");
 if (res.data.success) setProjects(res.data.data);
 };

 const fetchWorkOrders = async () => {
 const res = await api.get("/work-orders?limit=1000");
 if (res.data.success) setWorkOrders(res.data.data);
 };

 const fetchSheets = async () => {
 setLoading(true);
 try {
 const query = new URLSearchParams(filters).toString();
 const res = await api.get(`/measurement-sheets?${query}`);
 if (res.data.success) {
   const validSheets = (res.data.data || []).filter(
     (s) => s.project && (s.project._id || s.project.projectName)
   );
   setSheets(validSheets);
 }
 } catch (err) {
 console.error(err);
 } finally {
 setLoading(false);
 }
 };

 const handleFilterChange = (key, value) => {
    const newParams = new URLSearchParams(searchParams);
    if (key === "project") {
      if (value) {
        newParams.set("project", value);
        const currentWo = workOrders.find((w) => String(w._id) === String(filters.workOrder));
        const currentWoProjId = currentWo?.project?._id || currentWo?.project;
        if (currentWoProjId && String(currentWoProjId) !== String(value)) {
          newParams.delete("workOrder");
        }
      } else {
        newParams.delete("project");
      }
    } else if (key === "workOrder") {
      if (value) {
        newParams.set("workOrder", value);
        const selectedWo = workOrders.find((w) => String(w._id) === String(value));
        const autoProjId = selectedWo?.project?._id || selectedWo?.project;
        if (autoProjId) {
          newParams.set("project", String(autoProjId));
        }
      } else {
        newParams.delete("workOrder");
      }
    } else {
      if (value) newParams.set(key, value);
      else newParams.delete(key);
    }
    setSearchParams(newParams);
  };

 const downloadFile = async (id, type) => {
 try {
 setPreviewing(true);
 const res = await api.get(`/measurement-sheets/${id}/${type}`, { responseType: 'blob' });
 const blob = new Blob([res.data]);
 const url = window.URL.createObjectURL(blob);
 const link = document.createElement('a');
 link.href = url;
 
 // Try to get sheet number if possible, or use ID
 const sheet = sheets.find(s => s._id === id);
 const filename = (sheet?.sheetNumber || `JMR_${id}`).replace(/\//g, '_');
 
 link.setAttribute('download', `${filename}.${type === 'pdf' ? 'pdf' : 'xlsx'}`);
 document.body.appendChild(link);
 link.click();
 link.remove();
 setTimeout(() => URL.revokeObjectURL(url), 10000);
 } catch (err) {
 swal("Error", "Failed to download file", "error");
 } finally {
 setPreviewing(false);
 }
 };

 const handlePreviewPDF = async (id) => {
 try {
 setPreviewing(true);
 const res = await api.get(`/measurement-sheets/${id}/pdf`, { 
 responseType: "blob" 
 });
 const blob = new Blob([res.data], { type: "application/pdf" });
 const fileURL = URL.createObjectURL(blob);
 window.open(fileURL, "_blank");
 // Optional: Revoke URL after some time
 setTimeout(() => URL.revokeObjectURL(fileURL), 10000);
 } catch (err) {
 swal("Error", "Failed to preview PDF", "error");
 } finally {
 setPreviewing(false);
 }
 };

 const handleApprove = async (id) => {
 const confirm = await swal({
 title: "Approve Measurement Sheet?",
 text: "Signature is required for approval.",
 icon: "warning",
 buttons: ["Cancel", "Provide Signature"],
 });

 if (!confirm) return;

 setApproveId(id);
 setShowSignModal(true);
 };

 const handleSaveSignature = async (file) => {
 try {
 setLoading(true);

 const formData = new FormData();
 formData.append("signature", file);

 const res = await api.patch(
 `/measurement-sheets/${approveId}/approve`,
 formData,
 {
 headers: {
 "Content-Type": "multipart/form-data",
 },
 },
 );

 if (res.data.success) {
 swal(
 "Approved!",
 "Measurement Sheet approved successfully",
 "success",
 );
 setShowSignModal(false);
 setApproveId(null);
 fetchSheets();
 }
 } catch (err) {
 swal(
 "Error",
 err.response?.data?.message || "Failed to approve sheet",
 "error",
 );
 } finally {
 setLoading(false);
 }
 };

 const handleReject = async (id) => {
 const remarks = prompt("Enter rejection reason:");
 if (!remarks) return;

 try {
 const res = await api.patch(`/measurement-sheets/${id}/reject`, {
 remarks,
 });
 if (res.data.success) {
 swal(
 "Rejected!",
 "Measurement Sheet rejected successfully",
 "success",
 );
 fetchSheets();
 }
 } catch (err) {
 swal(
 "Error",
 err.response?.data?.message || "Failed to reject sheet",
 "error",
 );
 }
 };

 const handleDelete = async (id) => {
 const confirm = await swal({
 title: "Delete Measurement Sheet?",
 text: "Once deleted, you will not be able to recover this sheet!",
 icon: "warning",
 buttons: ["Cancel", "Delete"],
 dangerMode: true,
 });

 if (!confirm) return;

 try {
 const res = await api.delete(`/measurement-sheets/${id}`);
 if (res.data.success) {
 swal("Deleted!", "Measurement Sheet has been deleted", "success");
 fetchSheets();
 }
 } catch (err) {
 swal("Error", err.response?.data?.message || "Failed to delete sheet", "error");
 }
 };

 const getProjectName = (sheet) => {
 if (sheet.project?.projectName) return sheet.project.projectName;
 const project = projects.find(p => p._id === (sheet.project?._id || sheet.project));
 return project ? project.projectName : "N/A";
 };

  const getWorkOrderNo = (sheet) => {
    if (sheet.workOrder?.woNo || sheet.workOrder?.woNumber) return sheet.workOrder.woNo || sheet.workOrder.woNumber;
    const wo = workOrders.find(w => w._id === (sheet.workOrder?._id || sheet.workOrder));
    return wo ? (wo.woNo || wo.woNumber) : "—";
  };

 const getStatusBadge = (status) => {
 switch (status) {
 case "Approved": return <Badge bg="success">Approved</Badge>;
 case "Rejected": return <Badge bg="danger">Rejected</Badge>;
 case "Submitted": return <Badge bg="warning">Submitted</Badge>;
 default: return <Badge bg="secondary">Draft</Badge>;
 }
 };

 const getSheetTypeName = (sheet) => {
 // Standard sheet type names
 const names = {
 ELECTRICAL_LIGHTING: "Electrical Lighting",
 CONDUIT_MEASUREMENT: "Conduit Measurement",
 CONDUIT_SUMMARY: "Conduit Summary",
 SWITCH_SOCKET: "Switch & Socket",
 DB_INSTALLATION: "DB Installation",
 CABLE_MEASUREMENT: "Cable Measurement",
 CABLE_TRAY_SUMMARY: "Cable Tray Summary",
 WIRING_MEASUREMENT: "Wiring Measurement"
 };
 return names[sheet.sheetType] || sheet.sheetType;
 };

 const loadAutoGeneratePreview = async (sheetType = autoGenerateSheetType) => {
 if (!filters.workOrder) return;
 try {
 setPreviewLoading(true);
 const payload = {
 project: filters.project,
 workOrder: filters.workOrder,
 fromDate: filters.fromDate,
 toDate: filters.toDate,
 sheetType,
 };
 const res = await api.post("/measurement-sheets/auto-generate-from-dpr-preview", payload);
 if (res.data?.success) setAutoGeneratePreview(res.data.data);
 } catch (err) {
 setAutoGeneratePreview(null);
 swal("Error", err.response?.data?.message || err.message, "error");
 } finally {
 setPreviewLoading(false);
 }
 };

 const openAutoGenerateModal = async () => {
 if (!filters.workOrder) {
 swal("Warning", "Select a work order first", "warning");
 return;
 }
 setShowAutoGenerateModal(true);
 await loadAutoGeneratePreview(autoGenerateSheetType);
 };

 const handleGenerateAutoSheet = async () => {
 try {
 setGeneratingJmr(true);
 const payload = {
 project: filters.project,
 workOrder: filters.workOrder,
 fromDate: filters.fromDate,
 toDate: filters.toDate,
 sheetType: autoGenerateSheetType,
 };

 const res = await api.post("/measurement-sheets/auto-generate-from-dpr", payload);
 if (res.data?.success) {
 await swal("Success", res.data.message || "Measurement sheet auto generated successfully", "success");
 setShowAutoGenerateModal(false);
 const params = new URLSearchParams();
 if (filters.project) params.set("project", filters.project);
 if (filters.workOrder) params.set("workOrder", filters.workOrder);
 params.set("status", "Submitted");
 navigate(`/measurement-sheets?${params.toString()}`);
 }
 } catch (err) {
 swal("Error", err.response?.data?.message || err.message, "error");
 } finally {
 setGeneratingJmr(false);
 }
 };

 const svgMenu = (
    <svg width="20px" height="20px" viewBox="0 0 24 24">
      <circle fill="#000000" cx="5" cy="12" r="2"></circle>
      <circle fill="#000000" cx="12" cy="12" r="2"></circle>
      <circle fill="#000000" cx="19" cy="12" r="2"></circle>
    </svg>
  );

  const filteredWorkOrders = useMemo(() => {
    if (!filters.project) return workOrders;
    return workOrders.filter(
      (wo) => String(wo.project?._id || wo.project) === String(filters.project)
    );
  }, [workOrders, filters.project]);

  return (
 <Fragment>
 <PageTitle activeMenu="Measurement Sheets" motherMenu="JMR" />
 
 {/* =========================
 PDF PREVIEW LOADER
 ========================== */}
 {previewing && (
 <div
 className="position-fixed top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center"
 style={{
 background: "rgba(0,0,0,0.4)",
 zIndex: 9999,
 }}
 >
 <div className="bg-white p-4 rounded shadow text-center">
 <div className="spinner-border text-primary mb-3"></div>
 <h6 className="mb-0">Preparing JMR PDF…</h6>
 </div>
 </div>
 )}

 <Row>
 <Col lg={12}>
 <Card className="shadow-sm">
 <Card.Header className="bg-white py-3 d-flex justify-content-between align-items-center">
 <Card.Title className="text-primary fw-bold">Measurement Sheets (Detailed JMR)</Card.Title>
 <div className="d-flex gap-2 flex-wrap">
 <PermissionWrapper user={user} required={["create:jmrs"]}>
 <Button
 variant="warning"
 className="px-4"
 onClick={openAutoGenerateModal}
 disabled={generatingJmr || loading || !filters.workOrder}
 >
 <i className="fa fa-magic me-2"></i>
 {generatingJmr ? "Generating JMR..." : "Auto Generate JMR"}
 </Button>
 </PermissionWrapper>
 <PermissionWrapper user={user} required={["create:measurement-sheets"]}>
 <Button 
 variant="primary" 
 className="px-4"
 onClick={() => navigate(`/measurement-sheets/add?project=${filters.project}&workOrder=${filters.workOrder}`)}
 >
 <i className="fa fa-plus me-2"></i> Add Item
 </Button>
 </PermissionWrapper>
 </div>
 </Card.Header>
 <Card.Body>
 <Row className="mb-4 bg-light p-3 rounded mx-0 gy-3">
 <Col md={3}>
 <Form.Group>
 <Form.Label className="fw-bold">Project</Form.Label>
 <Select
 isClearable
 options={projects.map(p => ({ value: p._id, label: p.projectName }))}
 value={projects.filter(p => p._id === filters.project).map(p => ({ value: p._id, label: p.projectName }))[0]}
 onChange={(opt) => handleFilterChange("project", opt?.value)}
 styles={{ control: (base) => ({ ...base, minHeight: '31px' }) }}
 />
 </Form.Group>
 </Col>
 <Col md={3}>
 <Form.Group>
 <Form.Label className="fw-bold">Work Order</Form.Label>
								<Select
									isClearable
									options={filteredWorkOrders.map(wo => ({ value: wo._id, label: `${wo.woNo}${wo.label ? ` – ${wo.label}` : ""}` }))}
									value={workOrders.filter(wo => wo._id === filters.workOrder).map(wo => ({ value: wo._id, label: `${wo.woNo}${wo.label ? ` – ${wo.label}` : ""}` }))[0]}
									onChange={(opt) => handleFilterChange("workOrder", opt?.value)}
									styles={{ control: (base) => ({ ...base, minHeight: '31px' }) }}
								/>
 </Form.Group>
 </Col>
 <Col md={3}>
 <Form.Group>
 <Form.Label className="fw-bold">Status</Form.Label>
 <Form.Select
 size="sm"
 value={filters.status}
 onChange={(e) => handleFilterChange("status", e.target.value)}
 >
 <option value="">All Statuses</option>
 <option value="Draft">Draft</option>
 <option value="Submitted">Submitted</option>
 <option value="Approved">Approved</option>
 <option value="Rejected">Rejected</option>
 </Form.Select>
 </Form.Group>
 </Col>
 <Col md={2}>
 <Form.Group>
 <Form.Label className="fw-bold">From Date</Form.Label>
 <Form.Control
 type="date"
 size="sm"
 value={filters.fromDate}
 onChange={(e) => handleFilterChange("fromDate", e.target.value)}
 />
 </Form.Group>
 </Col>
 <Col md={2}>
 <Form.Group>
 <Form.Label className="fw-bold">To Date</Form.Label>
 <Form.Control
 type="date"
 size="sm"
 value={filters.toDate}
 onChange={(e) => handleFilterChange("toDate", e.target.value)}
 />
 </Form.Group>
 </Col>
 <Col md={2}>
 <Form.Group>
 <Form.Label className="fw-bold">Per Page</Form.Label>
 <Form.Select
 size="sm"
 value={filters.limit}
 onChange={(e) => handleFilterChange("limit", e.target.value)}
 >
 <option value="10">10</option>
 <option value="25">25</option>
 <option value="50">50</option>
 </Form.Select>
 </Form.Group>
 </Col>
 </Row>

 <Table responsive hover className="verticle-middle border rounded">
 <thead className="bg-primary text-dark">
 <tr>
 <th className="px-3">Sheet No</th>
 <th>Project</th>
 <th>Work Order</th>
 <th>Type</th>
 <th>Prepared Date</th>
 <th className="text-center">Status</th>
 <th className="text-center">Verified Copy</th>
 <th className="text-end px-3">Actions</th>
 </tr>
 </thead>
 <tbody>
 {loading ? (
 <tr><td colSpan={7} className="text-center py-4"><i className="fa fa-spinner fa-spin fa-2x"></i></td></tr>
 ) : sheets.length === 0 ? (
 <tr><td colSpan={7} className="text-center py-4 text-muted">No measurement sheets found</td></tr>
 ) : sheets.map((sheet) => (
 <tr key={sheet._id}>
 <td className="px-3"><strong>{sheet.sheetNumber}</strong></td>
 <td>{getProjectName(sheet)}</td>
 <td><Badge bg="light" text="dark" className="border">{getWorkOrderNo(sheet)}</Badge></td>
 <td>
 <Badge bg="info" className="text-white">
 {getSheetTypeName(sheet)}
 </Badge>
 </td>
 <td>{new Date(sheet.preparedDate).toLocaleDateString("en-IN")}</td>
 <td className="text-center">{getStatusBadge(sheet.status)}</td>
 <td className="text-center">
 <VerifiedCopyActions 
 id={sheet._id} 
 type="measurement-sheets" 
 hasVerifiedCopy={!!sheet.verifiedCopy?.filePath} 
 onUploadSuccess={fetchSheets}
 />
 </td>
 <td className="text-end px-3">
 <Dropdown drop="start i-false">
 <Dropdown.Toggle as="div" className="btn-link i-false" style={{ cursor: "pointer" }}>
 {svgMenu}
 </Dropdown.Toggle>
 <Dropdown.Menu align="end" as={PortalMenu}>
 <PermissionWrapper user={user} required={["read:measurement-sheets"]}>
 <Dropdown.Item onClick={() => handlePreviewPDF(sheet._id)}>
 <i className="fa fa-eye text-info me-2"></i> Preview PDF
 </Dropdown.Item>
 <Dropdown.Item onClick={() => downloadFile(sheet._id, 'pdf')}>
 <i className="fa fa-file-pdf-o text-danger me-2"></i> Download PDF
 </Dropdown.Item>
 <Dropdown.Item onClick={() => downloadFile(sheet._id, 'excel')}>
 <i className="fa fa-file-excel-o text-success me-2"></i> Download Excel
 </Dropdown.Item>
 </PermissionWrapper>

 {(sheet.status === "Draft" || sheet.status === "Submitted") && (
 <>
 <Dropdown.Divider />
 <PermissionWrapper user={user} required={["approve:measurement-sheets"]}>
 <Dropdown.Item onClick={() => handleApprove(sheet._id)} className="text-success">
 <i className="fa fa-check-circle me-2"></i> Approve Sheet
 </Dropdown.Item>
 </PermissionWrapper>

 <PermissionWrapper user={user} required={["reject:measurement-sheets"]}>
 <Dropdown.Item onClick={() => handleReject(sheet._id)} className="text-danger">
 <i className="fa fa-times-circle me-2"></i> Reject Sheet
 </Dropdown.Item>
 </PermissionWrapper>
 </>
 )}
 <Dropdown.Divider />
 <PermissionWrapper user={user} required={["edit:measurement-sheets"]}>
 <Dropdown.Item onClick={() => navigate(`/measurement-sheets/edit/${sheet._id}`)} disabled={sheet.status === "Approved"}>
 <i className="fa fa-edit text-primary me-2"></i> Edit Sheet
 </Dropdown.Item>
 </PermissionWrapper>

 <PermissionWrapper user={user} required={["delete:measurement-sheets"]}>
 <Dropdown.Item onClick={() => handleDelete(sheet._id)} className="text-danger">
 <i className="fa fa-trash text-danger me-2"></i> Delete Sheet
 </Dropdown.Item>
 </PermissionWrapper>
 </Dropdown.Menu>
 </Dropdown>
 </td>
 </tr>
 ))}
 </tbody>
 </Table>
 </Card.Body>
 </Card>
 </Col>
 </Row>

 <Modal show={showAutoGenerateModal} onHide={() => setShowAutoGenerateModal(false)} size="lg" centered>
 <Modal.Header closeButton>
 <Modal.Title>Auto Generate JMR</Modal.Title>
 </Modal.Header>
 <Modal.Body>
 <Row className="g-3 mb-3">
 <Col md={6}>
 <Form.Group>
 <Form.Label className="fw-bold">Sheet Type</Form.Label>
 <Form.Select
 value={autoGenerateSheetType}
 onChange={async (e) => {
 const nextType = e.target.value;
 setAutoGenerateSheetType(nextType);
 await loadAutoGeneratePreview(nextType);
 }}
 >
 {sheetTypes.map((type) => (
 <option key={type.code} value={type.code}>
 {type.name}
 </option>
 ))}
 </Form.Select>
 </Form.Group>
 </Col>
 <Col md={6}>
 <div className="border rounded p-3 bg-light h-100">
 <div className="fw-bold mb-1">Work Order</div>
 <div>{workOrders.find((wo) => wo._id === filters.workOrder)?.woNo || filters.workOrder || "-"}</div>
 <div className="mt-2 fw-bold mb-1">Preview</div>
 <div>
 {previewLoading
 ? "Loading preview..."
 : autoGeneratePreview?.approvedDprCount
 ? `${autoGeneratePreview.approvedDprCount} approved DPR(s), ${autoGeneratePreview.eligibleActivityCount || 0} eligible activities`
 : "No preview loaded yet"}
 </div>
 </div>
 </Col>
 </Row>

 <div className="table-responsive">
 <Table bordered hover size="sm">
 <thead className="table-light">
 <tr>
 <th>BOQ Item</th>
 <th>Activity</th>
 <th>Qty</th>
 <th>Unit</th>
 <th>Location</th>
 </tr>
 </thead>
 <tbody>
 {(autoGeneratePreview?.items || []).length ? (
 autoGeneratePreview.items.map((item, idx) => (
 <tr key={`${item.boqItem}-${idx}`}>
 <td>{item.itemName || "-"}</td>
 <td>{item.description || "-"}</td>
 <td>{Number(item.installedQty || 0).toFixed(2)}</td>
 <td>{item.unit || "-"}</td>
 <td>{item.location || "-"}</td>
 </tr>
 ))
 ) : (
 <tr>
 <td colSpan={5} className="text-center text-muted py-4">
 {previewLoading ? "Loading preview..." : "No preview items available"}
 </td>
 </tr>
 )}
 </tbody>
 </Table>
 </div>
 </Modal.Body>
 <Modal.Footer>
 <Button variant="secondary" onClick={() => setShowAutoGenerateModal(false)}>
 Close
 </Button>
 <Button variant="warning" onClick={handleGenerateAutoSheet} disabled={generatingJmr || previewLoading}>
 {generatingJmr ? "Generating..." : "Generate"}
 </Button>
 </Modal.Footer>
 </Modal>

 <SignatureModal
 show={showSignModal}
 onHide={() => {
 setShowSignModal(false);
 setApproveId(null);
 }}
 onSave={handleSaveSignature}
 title="Client Signature for Measurement Sheet Approval"
 />
 </Fragment>
 );
};

export default MeasurementSheetList;
